import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";

import { IAdobeInteraction, Video } from "@abbvie-epi/common/models";
import brightcovePlayerLoader from "@brightcove/player-loader";
import { AnalyticsHelperService } from "../../services/analytics-helper.service";

@Component({
    selector: "app-video-player",
    templateUrl: "./video-player.component.html",
    styleUrls: ["./video-player.component.scss"]
})
export class VideoPlayerComponent implements OnInit, OnDestroy, AfterViewInit {
    @Output() public videoClosed: EventEmitter<null>;
    @Input() analyticsTitle: string = "";
    @Input() video: Video;

    private element: any;

    private isReplay: boolean = false;
    private isClosed: boolean = false;
    private readonly BASE_ANALYTICS_ID: string = "base-video-interaction";

    @ViewChild("brightcoveContainer", { static: false })
    public brightcoveContainer!: ElementRef<HTMLDivElement>;

    private videoPlayer: any;

    private isFirstPlaythrough: boolean = false;
    private videoProgress: number = 0;

    constructor(private el: ElementRef, private analyticsHelper: AnalyticsHelperService) {
        this.element = el.nativeElement;
        this.videoClosed = new EventEmitter<null>();
    }

    ngOnInit(): void {}

    ngAfterViewInit(): void {
        this.loadOrCloseVideo(this.video);
    }

    ngOnDestroy(): void {
        this.destroyVideoPlayer();
    }

    private loadOrCloseVideo(video: Video | undefined): void {
        if (typeof video !== "undefined" && video.videoId !== "") {
            this.initBrightcove(video);
        } else {
            this.closeVideo();
        }
    }

    private initBrightcove(video: Video) {
        brightcovePlayerLoader({
            refNode: this.brightcoveContainer.nativeElement,
            refNodeInsert: "replace",
            accountId: video.brightcoveAccountId,
            playerId: video.brightcovePlayerId,
            embedId: "default",
            videoId: video.videoId
        })
            .then((success: any) => {
                this.videoPlayer = success.ref;
                this.videoPlayer.on("loadedmetadata", () => {
                    this.isFirstPlaythrough = true;
                });

                // logging
                this.videoPlayer.on("timeupdate", (event: any) => {
                    if (event.target.player.cache_) {
                        const { currentTime, duration } = event.target.player.cache_;
                        this.onVideoUpdate(currentTime, duration);
                    }
                });

                this.videoPlayer.on("play", (event: any) => {
                    if (event.target.player.cache_) {
                        const { currentTime, duration } = event.target.player.cache_;
                        this.videoPlayed(currentTime, duration);
                    }
                });

                this.videoPlayer.on("pause", (event: any) => {
                    if (event.target.player.cache_) {
                        const { currentTime, duration } = event.target.player.cache_;
                        this.videoPaused(currentTime, duration);
                    }
                });
            })
            .catch((err: any) => {
                console.error(err);
            });
    }

    // Completely clear out the video player and related listeners
    public destroyVideoPlayer() {
        if (this.videoPlayer) {
            this.videoPlayer.dispose();
            this.videoPlayer = null;
            this.videoProgress = 0;
        }
    }

    public async closeVideo() {
        this.isClosed = true;

        this.videoClosed.emit(null);
        this.destroyVideoPlayer();
    }

    // Video Playback Analytics Methods
    private async videoPlayed(currentTime: number, duration: number): Promise<void> {
        // Tracking
        if (this.isClosed) {
            return;
        }

        const ended = Math.round((currentTime / duration) * 100) === 100;

        const analyticsOverride: IAdobeInteraction = {
            component: {
                title: this.analyticsTitle
            },
            video: {
                name: this.analyticsTitle,
                id: this.video.videoId,
                length: duration.toString(),
                playback: currentTime.toString()
            }
        };

        if (this.isReplay) {
            // Replay
            this.videoProgress = 0;
            this.isReplay = false;

            analyticsOverride.link = {
                name: `${this.analyticsTitle} - Replay`,
                title: `${this.analyticsTitle} - Replay`
            };

            if (analyticsOverride.video) {
                analyticsOverride.video.action = "Replay";
            }

            analyticsOverride.mva = {
                ...this.getMvaProperty()
            };
        } else if (this.isFirstPlaythrough) {
            analyticsOverride.link = {
                name: `${this.analyticsTitle} - Start`,
                title: `${this.analyticsTitle} - Start`
            };

            if (analyticsOverride.video) {
                analyticsOverride.video.action = "Start";
            }

            analyticsOverride.mva = {
                ...this.getMvaProperty()
            };
        } else if (!ended) {
            analyticsOverride.link = {
                name: `${this.analyticsTitle} - Resume`,
                title: `${this.analyticsTitle} - Resume`
            };

            if (analyticsOverride.video) {
                analyticsOverride.video.action = "Resume";
            }
        } else {
            return;
        }

        this.analyticsHelper.trackInteraction(this.BASE_ANALYTICS_ID, analyticsOverride);
    }

    private videoPaused(currentTime: number, duration: number): void {
        if (this.isClosed) {
            return;
        }

        const ended = Math.round((currentTime / duration) * 100) === 100;

        const analyticsOverride: IAdobeInteraction = {
            component: {
                title: this.analyticsTitle
            },
            video: {
                name: this.analyticsTitle,
                id: this.video.videoId,
                length: duration.toString(),
                playback: currentTime.toString()
            }
        };

        if (ended) {
            this.isReplay = true;
            analyticsOverride.link = {
                name: `${this.analyticsTitle} - End`,
                title: `${this.analyticsTitle} - End`
            };
            if (analyticsOverride.video) {
                analyticsOverride.video.action = "End";
            }
        } else {
            this.isFirstPlaythrough = false;
            analyticsOverride.link = {
                name: `${this.analyticsTitle} - Pause`,
                title: `${this.analyticsTitle} - Pause`
            };

            if (analyticsOverride.video) {
                analyticsOverride.video.action = "Pause";
            }
        }

        this.analyticsHelper.trackInteraction(this.BASE_ANALYTICS_ID, analyticsOverride);
    }

    private async onVideoUpdate(currentTime: number, duration: number): Promise<void> {
        if (this.isClosed) {
            return;
        }

        const analyticsOverride: IAdobeInteraction = {
            component: {
                title: this.analyticsTitle
            },
            video: {
                name: this.analyticsTitle,
                id: this.video.videoId,
                length: duration.toString(),
                playback: currentTime.toString()
            }
        };
        const percentPlayed: number = Math.round((currentTime / duration) * 100);
        const prevProgress = this.videoProgress;
        if (percentPlayed >= 75) {
            this.videoProgress = 75;
        } else if (percentPlayed >= 50) {
            this.videoProgress = 50;
        } else if (percentPlayed >= 25) {
            this.videoProgress = 25;
        }
        if (this.videoProgress > prevProgress) {
            analyticsOverride.link = {
                name: `${this.analyticsTitle} - ${this.videoProgress}%`,
                title: `${this.analyticsTitle} - ${this.videoProgress}%`
            };

            if (analyticsOverride.video) {
                analyticsOverride.video.action = this.videoProgress.toString() + "%";
            }

            this.analyticsHelper.trackInteraction(this.BASE_ANALYTICS_ID, analyticsOverride);
        }
    }

    private getMvaProperty(): any {
        return {
            tier: "primary",
            type: "video",
            category: "Other",
            name: this.analyticsTitle
        };
    }
}
